import type { RouteRecordRaw } from 'vue-router';
import { permissionRouteGuard, featureFlagRouteGuard } from '@core/route-guards';
import { STUDIO_CRUD, STUDIO_ADMIN_CRUD } from '@core/models/permissions';

export const ORGANIZATION_OVERVIEW = 'organization-overview';
export const ORGANIZATION_PREPAY = 'organization-prepay';
export const ORGANIZATION_GALLERY = 'organization-gallery';
export const ORGANIZATION_JOBS = 'organization-jobs';
export const ORGANIZATION_JOB_GENERAL_SETTINGS = 'organization-jobsettings-general';
export const ORGANIZATION_JOB_PREORDER_SETTINGS = 'organization-jobsettings-preorder';
export const ORGANIZATION_JOB_PREORDER_FIELDS = 'organization-jobsettings-preorder-fields';
export const ORGANIZATION_JOB_GALLERY_SETTINGS = 'organization-jobsetting-gallery';
export const ORGANIZATION_JOB_STANDARD_SETTINGS = 'organization-jobsetting-standard';
export const ORGANIZATION_JOB_SUBJECT_OVERVIEW = 'organization-jobsettings-overview';
export const ORGANIZATION_JOB_OVERVIEW = 'organization-job-overview';
export const ORGANIZATION_JOB_PACKAGE_SETS = 'organization-job-package-sets';
export const ORGANIZATION_JOB_PACKAGE_SETS_PACKAGE_SET = 'organization-job-package-sets-package-set';
export const ORGANIZATION_JOB_PACKAGES = 'organization-job-packages';
export const ORGANIZATION_JOB_PACKAGES_LISTING = 'organization-job-packages-listing';
export const ORGANIZATION_JOB_PACKAGES_GREENSCREENS = 'organization-job-packages-greenscreens';
export const ORGANIZATION_JOB_SUBJECTS = 'organization-job-subjects';
export const ORGANIZATION_JOB_SUBJECTS_LISTING = 'organization-job-subjects-listing';
export const ORGANIZATION_JOB_SUBJECTS_GROUPS = 'organization-job-subjects-groups';
export const ORGANIZATION_JOB_ORDERS = 'organization-job-orders';
export const ORGANIZATION_JOB_PROMOTIONS = 'organization-job-promotions';
export const ORGANIZATION_JOB_WORKFLOWS = 'organization-job-workflows';
export const ORGANIZATION_JOB_ENTRY = 'organization-job-entry';
export const ORGANIZATION_JOB_ORDERS_PREPAY = 'organization-job-orders-prepay';
export const ORGANIZATION_JOB_ORDERS_BATCH = 'organization-job-orders-batch';
export const ORGANIZATION_JOB_ORDERS_INDIVIDUAL = 'organization-job-orders-individual';
export const ORGANIZATION_JOB_ORDERS_SUBMITTED = 'organization-job-orders-submitted';
export const ORGANIZATION_JOB_ORDERS_SUBJECT_CARTS = 'organization-job-orders-subject-carts';
export const DATA_IMPORT_JOB_TOOL = 'data-import-job-tool';
export const RESOLVE_STUDENTS_JOB_TOOL = 'resolve-students-job-tool';
export const ORGANIZATION_JOB_SUBJECTS_REPORT = 'organization-job-subjects-report';

export const routes: RouteRecordRaw[] = [
  {
    path: ':organizationId',
    component: () => import('./layouts/OrganizationEditorLayout.vue'),
    beforeEnter: [permissionRouteGuard([STUDIO_CRUD, STUDIO_ADMIN_CRUD])],
    children: [
      {
        path: 'overview',
        name: ORGANIZATION_OVERVIEW,
        component: () => import('./pages/OrganizationOverviewPage.vue'),
      },
      {
        path: 'prepay',
        name: ORGANIZATION_PREPAY,
        component: () => import('@apps/studioadmin/pages/PrepayListingPage.vue'),
      },
      {
        path: 'gallery',
        name: ORGANIZATION_GALLERY,
        component: () => import('@apps/studioadmin/pages/GalleryListingPage.vue'),
      },
      {
        path: 'jobs',
        beforeEnter: featureFlagRouteGuard('rspoJob', ORGANIZATION_OVERVIEW),
        meta: {
          breadCrumb: { label: 'Jobs' },
        },
        children: [
          {
            path: '',
            name: ORGANIZATION_JOBS,
            component: () => import('@apps/studioadmin/pages/StudioJobsPage.vue'),
          },
          {
            path: ':jobId',
            component: () => import('@apps/organization-editor/layouts/OrganizationJobEditorLayout.vue'),
            beforeEnter: [permissionRouteGuard([STUDIO_CRUD, STUDIO_ADMIN_CRUD])],
            children: [
              {
                name: ORGANIZATION_JOB_OVERVIEW,
                path: 'overview',
                component: () => import('@apps/job-editor/pages/JobOverviewPage.vue'),
              },
              {
                path: 'settings',
                component: () => import('@apps/job-editor/pages/Settings/JobSettingsPageV3.vue'),
                children: [
                  {
                    name: ORGANIZATION_JOB_GENERAL_SETTINGS,
                    path: '',
                    alias: 'general-settings',
                    component: () => import('@apps/job-editor/pages/Settings/components/GeneralSettingsV3.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_STANDARD_SETTINGS,
                    path: 'standard',
                    component: () => import('@apps/job-editor/pages/Settings/components/StandardSettingsV3.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_PREORDER_SETTINGS,
                    path: 'preorder',
                    component: () => import('@apps/job-editor/pages/Settings/components/PreorderSettingsV3.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_PREORDER_FIELDS,
                    path: 'prefields',
                    component: () => import('@apps/job-editor/pages/Settings/components/PreorderCustomFields.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_GALLERY_SETTINGS,
                    path: 'gallery',
                    component: () => import('@apps/job-editor/pages/Settings/components/GallerySettingsV3.vue'),
                  },
                ],
              },
              {
                path: 'package-sets',
                children: [
                  {
                    path: '',
                    name: ORGANIZATION_JOB_PACKAGE_SETS,
                    component: () => import('@apps/package-management/pages/PackageSetsPage.vue'),
                  },
                  {
                    path: ':packageSetId',
                    component: () => import('@apps/job-editor/layouts/PackageSetEditorLayout.vue'),
                    children: [
                      {
                        name: ORGANIZATION_JOB_PACKAGE_SETS_PACKAGE_SET,
                        path: '',
                        component: () => import('@apps/job-editor/pages/Packages/JobPackagesPageV3.vue'),
                      },
                    ],
                  },
                  {
                    name: ORGANIZATION_JOB_PACKAGES_GREENSCREENS,
                    path: 'greenscreens',
                    component: () => import('@apps/job-editor/pages/Packages/components/GreenScreenListing.vue'),
                  },
                ],
              },
              {
                name: DATA_IMPORT_JOB_TOOL,
                path: 'data-import',
                component: () => import('@apps/job-editor/pages/Data-Import/DataImportTools.vue'),
              },
              {
                name: RESOLVE_STUDENTS_JOB_TOOL,
                path: 'resolve-students',
                component: () => import('@apps/job-editor/pages/Resolve-Students/ResolveConsumerSubjectsPage.vue'),
              },
              {
                name: ORGANIZATION_JOB_SUBJECTS,
                path: 'subjects',
                component: () => import('@apps/job-editor/pages/Subjects/SubjectsPage.vue'),
                children: [
                  {
                    name: ORGANIZATION_JOB_SUBJECTS_LISTING,
                    path: '',
                    alias: `listing`,
                    component: () => import('@apps/job-editor/pages/Subjects/components/SubjectListing.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_SUBJECTS_REPORT,
                    path: 'report',
                    component: () => import('@apps/job-editor/pages/Subjects/SubjectsReportPage.vue'),
                  },
                  {
                    path: 'groups',
                    name: ORGANIZATION_JOB_SUBJECTS_GROUPS,
                    component: () => import('@apps/job-editor/pages/Subjects/components/GroupListing.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_SUBJECT_OVERVIEW,
                    path: ':subjectId',
                    component: () => import('@apps/job-editor/pages/Subjects/SubjectOverviewPage.vue'),
                  },
                ],
              },
              {
                name: ORGANIZATION_JOB_PROMOTIONS,
                path: 'promotions',
                component: () => import('@apps/job-editor/pages/Promotions/PromotionsPageV3.vue'),
              },
              {
                name: ORGANIZATION_JOB_WORKFLOWS,
                path: 'workflows',
                component: () => import('@apps/job-editor/pages/Workflows/WorkflowsPage.vue'),
              },
              {
                name: ORGANIZATION_JOB_ENTRY,
                path: 'entry',
                component: () => import('@apps/job-editor/pages/Entry/EntryPage.vue'),
              },
              {
                name: ORGANIZATION_JOB_ORDERS,
                path: 'orders',
                component: () => import('@apps/job-editor/pages/Orders/JobOrdersPage.vue'),
                children: [
                  {
                    name: ORGANIZATION_JOB_ORDERS_SUBJECT_CARTS,
                    path: '',
                    alias: `orders`,
                    component: () => import('@apps/job-editor/pages/Orders/SubjectCarts.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_ORDERS_SUBMITTED,
                    path: 'submitted',
                    component: () => import('@apps/job-editor/pages/Orders/SubmittedOrders.vue'),
                  },
                  // Legacy routes kept for backward compatibility but hidden in V3 UX
                  {
                    name: ORGANIZATION_JOB_ORDERS_PREPAY,
                    path: 'prepay',
                    component: () => import('@apps/job-editor/pages/Orders/PrepayOrders.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_ORDERS_BATCH,
                    path: 'batch',
                    component: () => import('@apps/job-editor/pages/Orders/BatchOrders.vue'),
                  },
                  {
                    name: ORGANIZATION_JOB_ORDERS_INDIVIDUAL,
                    path: 'individual',
                    component: () => import('@apps/job-editor/pages/Orders/IndividualOrders.vue'),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export default routes;
